import { LoadingOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { Spin } from 'antd';
import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';

import { PlusSvg } from '../../assets/icons';
import CuriBookmarksSvg from '../../assets/icons/CuriBookmarks';
import FolderSvg from '../../assets/icons/FolderSvg';
import theme from '../../assets/theme';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import useFolders from '../../hooks/useFolders';
import { useLocale } from '../../hooks/useLocale';
import { useMetrics } from '../../hooks/useMetrics';
import usePresentations, {
  currentPresentationsCacheKey,
} from '../../hooks/usePresentations';
import useRouterMatch from '../../hooks/useRouterMatch';
import { initSortCondition, saveSortCondition } from '../../utils/sortUtils';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import LiveEmptyState from '../_molecules/LiveEmptyState';
import { SortDropdownOption } from '../_molecules/SortDropdown';
import { FoldersSection } from '../_organisms/FolderSection';
import LessonBreadcrumbs from '../_organisms/LessonBreadcrumbs/LessonBreadcrumbs';
import { LessonsSection } from '../_organisms/LessonSection';
import {
  FolderSortingControls,
  NewFolder,
  sortConditionToQuery,
} from '../_organisms/SortingControls';
import { CreateButton } from './NewLessonsPage';

const FolderPage = () => {
  useLocale();
  const { t } = useTranslate();
  const isDesktop = useBreakPoint(800);
  const metrics = useMetrics();
  const { tenantId, parentFolderId, workspaceId, router } = useRouterMatch();

  const [sortCondition, setSortCondition] = useState<SortDropdownOption>(
    initSortCondition(),
  );

  const handleChangeSortCondition = (option: SortDropdownOption) => {
    setSortCondition(option);
    saveSortCondition(option);
  };

  const sortQuery = useMemo(
    () => ({
      isPrivate: true,
      parentId: parentFolderId,
      ...sortConditionToQuery(sortCondition),
    }),
    [parentFolderId, sortCondition],
  );

  const { ref: fetchMorePresentationsRef, inView } = useInView();

  const {
    presentations,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePresentations(sortQuery);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const {
    currentFolders,
    allFolders,
    isFetchingFolders,
    isDeletingFolder,
    breadcrumbs,
    deleteFolder,
    saveFolder,
    isSavingFolder,
  } = useFolders(parentFolderId);

  const isLoadingData = isFetchingFolders || isFetching;

  const presentationsCacheKey = useMemo(() => currentPresentationsCacheKey(sortQuery), [
    sortQuery,
  ]);

  const currentFolder = useMemo(() => {
    return allFolders?.find(f => f.id === parentFolderId);
  }, [allFolders, parentFolderId]);

  const sortedFolders = useMemo(() => {
    return currentFolders.sort((a, b) => {
      if (!a.title || !b.title) return -1;
      if (sortCondition.value === 'alphaAsc') {
        return a.title?.localeCompare(b.title || '');
      } else if (sortCondition.value === 'alphaDesc') {
        return b.title?.localeCompare(a.title || '');
      } else if (sortCondition.value === 'newestModified') {
        return new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime();
      } else if (sortCondition.value === 'oldestModified') {
        return new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime();
      }
      return 0;
    });
  }, [currentFolders, sortCondition]);

  const createLessonLink = useMemo(() => {
    if (parentFolderId && workspaceId)
      return `/${tenantId}/lessons/new/edit?workspaceId=${workspaceId}&parentFolderId=${parentFolderId}`;
    if (workspaceId) return `/${tenantId}/lessons/new/edit?workspaceId=${workspaceId}`;
    if (parentFolderId)
      return `/${tenantId}/lessons/new/edit?parentFolderId=${parentFolderId}`;
    return `/${tenantId}/lessons/new/edit`;
  }, [parentFolderId, tenantId, workspaceId]);

  return (
    <>
      <Head>
        <title>
          {t('folder', 'Folder')} | {currentFolder?.title}
        </title>
      </Head>
      <PageContainer>
        <ContentContainer $isDesktop={isDesktop}>
          <HeaderContainer>
            <HeaderTitle>
              <IconWrapper height="20px" width="20px">
                <FolderSvg />
              </IconWrapper>
              {breadcrumbs && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <LessonBreadcrumbs breadcrumbs={breadcrumbs} />
                </div>
              )}
            </HeaderTitle>
            <ControlsRow>
              <ButtonGroup>
                <CreateButton
                  onClick={() => {
                    metrics.logEvent('ControlsRow.GetLessonClicked', {
                      from: 'folderPage',
                    });
                    router.push(`/${tenantId}/home`);
                  }}
                  icon={<PlusSvg />}
                >
                  <T keyName="Get lesson" defaultValue="Get lesson" />
                  <span
                    style={{
                      fontSize: 12,
                      color: theme.colors.orange,
                      background: 'rgba(255,255,255,0.8)',
                      borderRadius: 7,
                      padding: '0 8px',
                      marginLeft: 10,
                    }}
                  >
                    <T keyName="AI" defaultValue="AI" />
                  </span>
                </CreateButton>

                <Link href={createLessonLink}>
                  <a>
                    <Button
                      height={'42px'}
                      type="ghost"
                      styles={{
                        paddingLeft: 16,
                        paddingRight: 16,
                        boxShadow: theme.effects.floatingCreatorElementShadow,
                      }}
                      onClick={() => {
                        metrics.logEvent('ControlsRow.NewFromBlankClicked', {
                          from: 'folderPage',
                        });
                      }}
                    >
                      <T keyName="+ New from blank" defaultValue="+ New from blank" />
                    </Button>
                  </a>
                </Link>
                <div
                  style={{ height: '42px', width: '1px', background: 'rgb(217,217,217)' }}
                ></div>
                <NewFolder />
              </ButtonGroup>
            </ControlsRow>
            <div
              style={{ height: '1px', width: '100%', background: 'rgb(217,217,217)' }}
            ></div>
            <ControlsRow style={{ marginTop: 20 }}>
              <FolderSortingControls
                onChange={handleChangeSortCondition}
                sortCondition={sortCondition}
                currentFolder={currentFolder}
              />
            </ControlsRow>
          </HeaderContainer>

          {sortedFolders.length > 0 && (
            <div>
              <Title $fontSize={16}>Folders</Title>
              <FoldersSection
                folders={sortedFolders}
                isDeletingFolder={isDeletingFolder || false}
                deleteFolder={deleteFolder}
                allFolders={allFolders}
                saveFolder={saveFolder}
                isSavingFolder={isSavingFolder}
                type="lessons"
              />
            </div>
          )}
          {presentations && presentations.length > 0 && (
            <div>
              <Title $fontSize={16}>Lessons</Title>
              <LessonsSection
                lessons={presentations}
                presentationsCacheKey={presentationsCacheKey}
              />
            </div>
          )}
          {presentations !== undefined &&
            presentations.length === 0 &&
            allFolders !== undefined &&
            sortedFolders.length === 0 &&
            !isLoadingData && (
              <div>
                <EmptyStateContainer $isGeneratorRow={true}>
                  <LiveEmptyState
                    maxWidth={500}
                    icon={<CuriBookmarksSvg />}
                    title={t('addLesson', 'Add a lesson to get started')}
                  >
                    <CreateButton
                      onClick={() => {
                        metrics.logEvent('ControlsRow.GetLessonClicked', {
                          from: 'folderPage',
                        });
                        router.push(`/${tenantId}/home`);
                      }}
                      style={{ width: 200 }}
                      icon={<PlusSvg />}
                    >
                      <T keyName="Get lesson" defaultValue="Get lesson" />
                      <span
                        style={{
                          fontSize: 12,
                          color: theme.colors.orange,
                          background: 'rgba(255,255,255,0.8)',
                          borderRadius: 7,
                          padding: '0 8px',
                          marginLeft: 10,
                        }}
                      >
                        <T keyName="AI" defaultValue="AI" />
                      </span>
                    </CreateButton>
                  </LiveEmptyState>
                </EmptyStateContainer>
              </div>
            )}
          <div ref={fetchMorePresentationsRef}>
            {isFetchingNextPage && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 50,
                }}
              >
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 20, color: theme.colors.black }}
                      spin
                    />
                  }
                />
              </div>
            )}
          </div>
          <div style={{ height: 20, width: '100%' }}>
            {/* Render this empty div to make sure the inView div triggers fetch more  */}
          </div>
        </ContentContainer>
      </PageContainer>
    </>
  );
};

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 800px) {
    justify-content: center;
    flex-wrap: wrap;
  }
  margin-bottom: 20px;
`;

const HeaderContainer = styled.div`
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${theme.colors.black};
  margin: 0 0 0px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 800px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const EmptyStateContainer = styled.div<{ $isGeneratorRow?: boolean }>`
  div > svg {
    height: 150px !important;
    width: 200px !important;
  }

  ${({ $isGeneratorRow }) =>
    $isGeneratorRow &&
    css`
      padding: 0px 20px;
      > div {
        margin-top: 0px;
        padding: 10px 0px;
        min-height: fit-content;
        div > svg {
          height: 70px !important;
          width: 100px !important;
        }
        p {
          margin: 0px;
        }
        h3 {
          margin-top: 5px !important;
        }
      }
    `}

  @media (max-width: 800px) {
    div > svg {
      height: 100px !important;
      width: 150px !important;
    }
  }
`;

export default FolderPage;

const PageContainer = styled.div`
  background-color: ${theme.colors.background};
  height: inherit;
  width: 100%;
  height: 100%;

  @media (max-width: 900px) {
    padding-top: 60px;
  }
`;

const ContentContainer = styled.div<{
  $isDesktop?: boolean;
}>`
  width: 100%;
  height: 100%;
  padding-bottom: 20px;

  ${({ $isDesktop }) =>
    $isDesktop &&
    css`
      padding-left: 240px;
    `}
  ${({ $isDesktop }) =>
    !$isDesktop &&
    css`
      padding-left: 0px;
    `}
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  text-transform: uppercase;
  padding-left: 30px;

  @media (max-width: 800px) {
    padding-left: 20px;
    margin-bottom: 0px;
  }

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 2}px;
  }
`;
